import type {
  UpdateNutritionPlanMarketplaceConfigArgs,
  FetchNutritionPlanByIdArgs,
  UnarchiveNutritionPlanArgs,
  ArchiveNutritionPlanArgs,
  CreateNutritionPlanArgs,
  FetchNutritionPlansArgs,
  EditNutritionPlanArgs,
} from './types/nutritionPlans.types';
import type {
  FetchSignedUploadUrlArgs,
  UiSignedUploadUrl,
} from './types/files.types';
import type { ApiNutritionPlan } from '../schemas/nutritionPlans.schema';
import type { BaseApiResponse } from './types/api.types';

import {
  createNutritionPlanApiArgs,
  mapNutritionPlanFromApi,
} from './mappers/nutritionPlans.mappers';
import { postWithPagination, destroy, patch, post, get } from './axios';
import { DEFAULT_CURRENCY } from '../config/constants';

export const createNutritionPlan = async ({
  locale,
  organizationId,
  ...args
}: CreateNutritionPlanArgs) => {
  const response = await post<ApiNutritionPlan>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/nutrition-plans`,
    createNutritionPlanApiArgs(args),
  );
  if (!response?.data) return null;
  return mapNutritionPlanFromApi(response.data, locale);
};

export const editNutritionPlan = async ({
  id,
  locale,
  organizationId,
  ...args
}: EditNutritionPlanArgs) => {
  const response = await patch<ApiNutritionPlan>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/nutrition-plans/${id}`,
    createNutritionPlanApiArgs(args),
  );
  if (!response?.data) return null;
  return mapNutritionPlanFromApi(response.data, locale);
};

export const fetchNutritionPlanById = async ({
  id,
  locale,
  organizationId,
}: FetchNutritionPlanByIdArgs) => {
  const response = await get<BaseApiResponse<ApiNutritionPlan>>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/nutrition-plans/${id}`,
  );
  if (!response?.data) return null;
  return mapNutritionPlanFromApi(response.data, locale);
};

export const fetchNutritionPlans = async ({
  contentFilters,
  locale,
  organizationId,
  tableFilters,
}: FetchNutritionPlansArgs) => {
  const { search, ...restTableFilters } = tableFilters;
  const { authorIds, isDeleted, isHome, isMarketplace, sources, tags } =
    contentFilters ?? {};

  const response = await postWithPagination<ApiNutritionPlan[]>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/nutrition-plans/list`,
    {
      ...restTableFilters,
      ...(search ? { search } : {}),
      ...(tags ? { tagIds: tags } : {}),
      authorIds,
      isDeleted,
      isHome,
      isMarketplace,
      sources,
    },
  );
  if (!response?.data) return null;
  return {
    ...response,
    data: response.data.map((plan) => mapNutritionPlanFromApi(plan, locale)),
  };
};

export const fetchNutritionPlanSignedUploadUrl = async ({
  mediaType,
  organizationId,
  target,
}: FetchSignedUploadUrlArgs) => {
  const response = await get<{ data: UiSignedUploadUrl }>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/nutrition-plans/signed-upload-url?mediaType=${mediaType}${target ? `&target=${target}` : ''}`,
  );
  return response?.data as UiSignedUploadUrl;
};

export const archiveNutritionPlan = async ({
  id,
  organizationId,
}: ArchiveNutritionPlanArgs) => {
  await destroy(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/nutrition-plans/${id}`,
  );
  return true;
};

export const unarchiveNutritionPlan = async ({
  id,
  organizationId,
}: UnarchiveNutritionPlanArgs) => {
  await patch(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/nutrition-plans/${id}/restore`,
  );
  return true;
};

export const updateNutritionPlanMarketplaceConfig = async ({
  id,
  currency = DEFAULT_CURRENCY,
  enabled,
  organizationId,
  price,
}: UpdateNutritionPlanMarketplaceConfigArgs) => {
  await patch(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/nutrition-plans/${id}`,
    {
      configuration: {
        marketplace: { currency, enabled, price: enabled ? price : 0 },
      },
    },
  );
};
